// @ts-check
import classNames from 'classnames';
import React from 'react';
import { ConstructBasicElement, GetCopyFromJson } from '~/contentstack/rendering';

import './SingleAlert.scss';

/**
 * @typedef SingleAlert
 * @property {string} [heading] The heading of the Alert.
 * @property {import('~/contentstack').JsonArgs} copy The copy of the alert.
 * @property {boolean} [include_warning_icon=true] A value indicating whether the Alert Icon should be visible on the left.
 * @property {Date} [active_by]
 * @property {Date} [expires_by]
 * @property {import('~/contentstack/types').ModelWithLivePreviewAttr<import('~/templates/CSTypes').CSLink>}  [click_destination]
 *
 * @typedef SingleAlertProps
 * @property {import('~/contentstack/types').ModelWithLivePreviewAttr<SingleAlert>} alert
 */
/** @type {import('~/types/React').ReactFC<SingleAlertProps>} */
const SingleAlert = ({ alert, ...rest }) => {
	const copy = GetCopyFromJson(alert.copy, ConstructBasicElement('p'));

	const now = new Date();
	const activeBy = !!alert.active_by ? new Date(alert.active_by) : null;
	const expiresBy = !!alert.expires_by ? new Date(alert.expires_by) : null;

	const isAfterActive = activeBy < now;
	const isBeforeExpire = expiresBy > now;

	// Skipping alert - is not after active date or not before expire
	if ((!!activeBy && !isAfterActive) || (!!expiresBy && !isBeforeExpire)) {
		return null;
	}

	const content = (
		<div aria-labelledby="alert" role="alert" className="alert-container container">
			{alert.include_warning_icon && (
				<div {...alert?.$?.include_warning_icon}>
					<span aria-hidden="true" className="alert-icon icon">
						warning
					</span>
				</div>
			)}
			<div>
				{!!alert.heading && (
					<h2 className="alert-heading" {...alert.$?.heading}>
						{alert.heading}
					</h2>
				)}

				<div className="alert-copy">{copy}</div>
			</div>
		</div>
	);

	if (!alert.click_destination?.href) {
		return (
			// @ts-ignore - This is a DateTime field, and the type thinks it's supposed to have children.
			<div {...rest} className={classNames(rest.className, 'single-alert')} {...alert.$?.active_by}>
				{content}
			</div>
		);
	}

	return (
		<a {...rest} className={classNames(rest.className, 'single-alert')} {...alert.click_destination.$?.title}>
			{content}
		</a>
	);
};

export default SingleAlert;
